import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { ScoreRing } from "@/components/ScoreRing";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useModals } from "@/context/ModalContext";
import { useSaved } from "@/context/SavedContext";
import { Bookmark, Trash2, Copy, Check, GitCompare, X } from "lucide-react";

export default function Saved() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openAuth } = useModals();
  const { saved, removeSaved } = useSaved();
  const [compare, setCompare] = useState([]);
  const [copied, setCopied] = useState(null);

  const copy = (name) => {
    navigator.clipboard?.writeText(name);
    setCopied(name);
    setTimeout(() => setCopied(null), 1300);
  };

  const toggleCompare = (item) => {
    setCompare((prev) =>
      prev.find((c) => c.id === item.id) ? prev.filter((c) => c.id !== item.id) : prev.length < 4 ? [...prev, item] : prev
    );
  };

  if (!user) {
    return (
      <div className="max-w-[560px] mx-auto px-4 sm:px-6 py-24 text-center">
        <Seo title="Saved names — Namable" description="Your saved name shortlist." path="/saved" />
        <div className="w-14 h-14 rounded-[16px] bg-brand-soft text-brand flex items-center justify-center mx-auto">
          <Bookmark size={26} />
        </div>
        <h1 className="font-display text-[24px] font-extrabold text-ink mt-5">Save your favourite names</h1>
        <p className="text-ink-muted text-[15px] mt-2">Create a free account to build your shortlist and access it anywhere.</p>
        <Button className="mt-6 h-12 px-6 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold btn-glow" onClick={() => openAuth({ mode: "register" })} data-testid="saved-signup-btn">
          Create free account
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-10 sm:py-14 pb-28">
      <Seo title="Saved names — Namable" description="Your saved name shortlist." path="/saved" />
      <p className="overline text-brand">Your shortlist</p>
      <h1 className="font-display text-[30px] font-extrabold tracking-[-0.02em] text-ink mt-1.5">Saved names</h1>
      <p className="text-ink-muted text-[14px] mt-1.5">{saved.length} saved · select up to 4 to compare.</p>

      {saved.length === 0 ? (
        <div className="card-soft p-10 text-center mt-8">
          <p className="text-ink-muted">No saved names yet.</p>
          <Button className="mt-4 rounded-lg bg-brand text-white font-bold" onClick={() => navigate("/generate")}>
            Generate names
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 mt-7">
          {saved.map((s) => {
            const selected = compare.find((c) => c.id === s.id);
            return (
              <div key={s.id} className={`card-soft p-5 transition-all ${selected ? "ring-2 ring-brand" : ""}`} data-testid={`saved-card-${s.name}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <h3 className="font-display text-[20px] font-extrabold tracking-[-0.02em] text-ink truncate">{s.name}</h3>
                    <p className="text-[13px] text-ink-subtle mt-0.5">{s.domain}</p>
                  </div>
                  <ScoreRing score={s.score || 0} />
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3.5 border-t hairline">
                  <button
                    onClick={() => toggleCompare(s)}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[13px] font-semibold transition-colors ${
                      selected ? "bg-brand-soft text-brand" : "bg-canvas-alt text-ink-muted hover:text-ink"
                    }`}
                    data-testid={`compare-toggle-${s.name}`}
                  >
                    <GitCompare size={14} /> {selected ? "Selected" : "Compare"}
                  </button>
                  <button onClick={() => copy(s.name)} className="w-9 h-9 rounded-lg bg-canvas-alt text-ink-muted hover:text-ink flex items-center justify-center" data-testid={`saved-copy-${s.name}`}>
                    {copied === s.name ? <Check size={15} className="text-success" /> : <Copy size={15} />}
                  </button>
                  <button onClick={() => removeSaved(s.id)} className="w-9 h-9 rounded-lg bg-canvas-alt text-ink-muted hover:text-danger flex items-center justify-center" data-testid={`saved-remove-${s.name}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* compare tray */}
      {compare.length > 0 && (
        <div className="fixed bottom-4 left-0 right-0 px-4 z-30">
          <div className="max-w-[760px] mx-auto card-soft shadow-card p-4 flex items-center gap-4 overflow-x-auto" data-testid="compare-tray">
            <span className="text-[12px] font-bold text-ink-subtle uppercase tracking-wide shrink-0">Compare</span>
            {compare.map((c) => (
              <div key={c.id} className="flex items-center gap-2 shrink-0">
                <ScoreRing score={c.score || 0} size={36} />
                <div>
                  <p className="text-[14px] font-bold text-ink leading-tight">{c.name}</p>
                  <p className="text-[11px] text-ink-subtle">{c.domain}</p>
                </div>
              </div>
            ))}
            <button onClick={() => setCompare([])} className="ml-auto shrink-0 w-8 h-8 rounded-full bg-canvas-alt text-ink-muted flex items-center justify-center" data-testid="clear-compare">
              <X size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
